import React, { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

// Components
import {
    Tooltip,
    Button,
    Icon,
    Empty,
    Layout,
    Card,
    Breadcrumb,
    Modal,
    Row,
    Col,
    Table,
    Spin
} from 'antd';
import AddOrEditHomeModal from './AddOrEditHomeModal';

// Actions
import {
    fetchAllHomes,
    onAddOrEditModalIsShown,
    handleDeleteHome,
    onAddOrEditModalIsEdit,
    onAddOrEditModalSelectedRow
} from '../../data/homes/actions';

// Selectors
import { getHomesList } from '../../data/homes/selectors';

// Others
import { strings } from '../../localization';
import { ROUTE_NAMES, formatPattern } from '../../utils';

const ButtonGroup = Button.Group;

class Homes extends Component {
    state = {
        deleteModalShown: false,
        deleteModalIsLoading: false,
        recordToDelete: null
    };

    componentWillMount() {
        const { fetchAllHomes } = this.props;
        fetchAllHomes();
    }

    handleDetail = record => () => {
        const { push } = this.props;
        push(formatPattern(ROUTE_NAMES.HOME_DETAIL, { homeId: record.id }));
    };

    toggleAddOrEditModal = (isShown, record = undefined) => () => {
        const {
            onAddOrEditModalIsShown,
            onAddOrEditModalIsEdit,
            onAddOrEditModalSelectedRow
        } = this.props;

        onAddOrEditModalIsShown(isShown);

        if (record) {
            onAddOrEditModalIsEdit(true);
            onAddOrEditModalSelectedRow(record);
        }
    };

    onDeleteModalShownToggle = (isShown, record) => () => {
        this.setState({ deleteModalShown: isShown, recordToDelete: record || null });
    };

    onDeleteModalStateReset = () => {
        this.setState(() => ({
            deleteModalShown: false,
            deleteModalIsLoading: false,
            recordToDelete: null
        }));
    };

    onDeleteHome = () => {
        this.setState({ deleteModalIsLoading: true });

        const { recordToDelete } = this.state;
        const { params, fetchAllHomes, handleDeleteHome } = this.props;
        handleDeleteHome(recordToDelete.id, params).then(() => {
            this.onDeleteModalStateReset();
            fetchAllHomes(params);
        });
    };

    isAllowedToEdit = record => {
        const { user } = this.props;
        const userId = user.get('id');
        const isAdmin = user.get('admin');
        const isInstaller = user.get('installer');
        return record.owner === userId || isAdmin || isInstaller;
    };

    render() {
        const columns = [
            {
                title: strings.id,
                dataIndex: 'id',
                key: 'id'
            },
            {
                title: strings.homes.name,
                dataIndex: 'name',
                key: 'name'
            },
            {
                title: strings.homes.postalCode,
                dataIndex: 'postalCode',
                key: 'postalCode'
            },
            {
                title: strings.actionsLabel,
                key: 'action',
                className: 'text-right',
                width: 250,
                render: (text, record) => (
                    <span style={{ float: 'right' }}>
                        <ButtonGroup style={{ marginRight: 10 }}>
                            <Button
                                data-testid="homes-edit-button"
                                type="primary"
                                disabled={!this.isAllowedToEdit(record)}
                                onClick={this.toggleAddOrEditModal(true, record)}
                            >
                                <Icon type="edit" />
                            </Button>
                        </ButtonGroup>
                        <ButtonGroup style={{ marginRight: 10 }}>
                            <Button
                                data-testid="homes-delete-button"
                                type="danger"
                                disabled={!this.isAllowedToEdit(record)}
                                onClick={this.onDeleteModalShownToggle(true, record)}
                            >
                                <Icon type="delete" />
                            </Button>
                        </ButtonGroup>
                        <Tooltip placement="right" title={strings.homes.showHomeDetail}>
                            <Button
                                onClick={this.handleDetail(record)}
                                data-testid="homes-detail-button"
                                type="primary"
                                ghost
                            >
                                {strings.homes.detail.homeDetail}
                                <Icon type="right" />
                            </Button>
                        </Tooltip>
                    </span>
                )
            }
        ];

        const { homesAreLoading, homesData, addOrEditModalIsShown, match } = this.props;
        const { deleteModalShown, deleteModalIsLoading, recordToDelete } = this.state;
        return (
            <React.Fragment>
                <Layout key="admin-users-content">
                    <Layout.Content style={{ padding: '10px' }}>
                        <Card style={{ marginBottom: '30px' }}>
                            <h2 data-testid="headline">{strings.homes.routeTitle}</h2>
                            <Breadcrumb separator=">">
                                <Breadcrumb.Item>{strings.appName}</Breadcrumb.Item>
                                <Breadcrumb.Item>
                                    <Icon type="deployment-unit" />
                                    &nbsp;
                                    {strings.homes.route}
                                </Breadcrumb.Item>
                            </Breadcrumb>
                        </Card>
                        <Row>
                            <Col xs={24}>
                                <Card
                                    bodyStyle={{ padding: 0 }}
                                    loading={homesAreLoading}
                                    title={
                                        <span style={{ lineHeight: '42px' }}>
                                            <Icon type="home" /> {strings.homes.listOfHomes}
                                        </span>
                                    }
                                    extra={
                                        !homesAreLoading && [
                                            <Button
                                                key="add-home"
                                                size="large"
                                                type="primary"
                                                onClick={this.toggleAddOrEditModal(true)}
                                                data-testid="homes-add-button"
                                            >
                                                <Icon type="plus" />
                                                {strings.homes.addHome}
                                            </Button>
                                        ]
                                    }
                                >
                                    {homesData && homesData.length ? (
                                        <Table
                                            pagination={false}
                                            dataSource={homesData}
                                            columns={columns}
                                            rowKey="id"
                                        />
                                    ) : (
                                        <Empty
                                            description={<span>{strings.noDataAvailable}</span>}
                                            style={{ margin: '30px auto' }}
                                        />
                                    )}
                                </Card>
                            </Col>
                        </Row>
                    </Layout.Content>
                </Layout>

                {addOrEditModalIsShown && (
                    <AddOrEditHomeModal
                        msgs={strings}
                        isShown={addOrEditModalIsShown}
                        params={match.params}
                        onCancel={this.toggleAddOrEditModal(false)}
                    />
                )}
                {recordToDelete && (
                    <Modal
                        key="delete-user-modal"
                        visible={deleteModalShown}
                        onCancel={this.onDeleteModalStateReset}
                        onOk={this.onDeleteHome}
                        okText={strings.delete}
                        okType="danger"
                        confirmLoading={deleteModalIsLoading}
                        cancelText={strings.cancel}
                        title={strings.sureToDeleteTitle}
                    >
                        <Spin spinning={deleteModalIsLoading} size="large">
                            <div>
                                <p>
                                    {strings.homes.sureToDeleteContent}{' '}
                                    <strong>{recordToDelete.name}</strong>?
                                </p>
                            </div>
                        </Spin>
                    </Modal>
                )}
            </React.Fragment>
        );
    }
}
export default connect(
    (state, ownProps) => ({
        ...ownProps,
        addOrEditModalIsShown: state.homes.getIn(['addOrEditModal', 'isShown']),
        homesAreLoading: state.homes.getIn(['list', 'isLoading']),
        homesData: getHomesList(state),
        user: state.auth.getIn(['user', 'data'])
    }),
    {
        push,
        onAddOrEditModalIsShown,
        onAddOrEditModalIsEdit,
        onAddOrEditModalSelectedRow,
        fetchAllHomes,
        handleDeleteHome
    }
)(Homes);